import { useEffect, useState } from 'react';
import { apiClient, ApiError, type MeResponse } from '@/lib/api/client';
import { SignInScreen } from '@/features/auth/SignInScreen';
import { Dashboard } from '@/features/dashboard/Dashboard';
import { InviteAcceptModal } from '@/features/invite/InviteAcceptModal';
import { UndoProvider } from '@/lib/undo';

type AuthState =
  | { kind: 'loading' }
  | { kind: 'signed_out'; initialError: string | null; inviteToken: string | null }
  | { kind: 'signed_in'; me: MeResponse; inviteToken: string | null };

function readQueryParam(name: string): string | null {
  return new URLSearchParams(window.location.search).get(name);
}

function clearAuthQueryParams() {
  const url = new URL(window.location.href);
  ['signed_in', 'welcome', 'error', 'invite'].forEach((k) => url.searchParams.delete(k));
  window.history.replaceState({}, '', url.toString());
}

/** Strip transient auth-result params (error / signed_in / welcome) from the
 * URL after they've been captured into React state. Without this, a failed
 * verify leaves `?error=internal_error` sticky — every refresh re-renders the
 * scary "Something went wrong" banner even though the user could just try
 * again. Leaves `invite` alone since it stays useful across the auth round-trip. */
function stripTransientAuthParams() {
  const url = new URL(window.location.href);
  let changed = false;
  ['signed_in', 'welcome', 'error'].forEach((k) => {
    if (url.searchParams.has(k)) {
      url.searchParams.delete(k);
      changed = true;
    }
  });
  if (changed) window.history.replaceState({}, '', url.toString());
}

function App() {
  const [auth, setAuth] = useState<AuthState>({ kind: 'loading' });

  async function reload() {
    try {
      const me = await apiClient.me();
      const invite = readQueryParam('invite');
      stripTransientAuthParams();
      setAuth({ kind: 'signed_in', me, inviteToken: invite });
    } catch (err) {
      const invite = readQueryParam('invite');
      const errorParam = readQueryParam('error');
      stripTransientAuthParams();
      // 401 is the normal "not signed in" path — never log, never alarm.
      // Anything else (5xx, network, CORS) is unexpected: surface to console
      // for debugging but DO NOT shove a scary banner onto the login screen.
      // The user can still try to sign in; if the API is truly down, the
      // magic-link request will fail and surface its own error then.
      if (!(err instanceof ApiError && err.status === 401)) {
        // eslint-disable-next-line no-console
        console.error('[apetech-pm] /v1/me failed unexpectedly:', err);
      }
      setAuth({ kind: 'signed_out', initialError: errorParam, inviteToken: invite });
    }
  }

  useEffect(() => {
    reload();
  }, []);

  if (auth.kind === 'loading') {
    return (
      <main className="grid min-h-dvh place-items-center bg-background text-foreground">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          Loading…
        </div>
      </main>
    );
  }

  if (auth.kind === 'signed_out') {
    // If user has an invite token but is signed out, they need to sign in
    // FIRST (with the email the invite was sent to). After they sign in we'll
    // detect the ?invite= still in the URL and show the accept modal.
    return <SignInScreen initialError={auth.initialError} />;
  }

  // signed_in
  return (
    <UndoProvider>
      <Dashboard
        me={auth.me}
        onSignOut={() => setAuth({ kind: 'signed_out', initialError: null, inviteToken: null })}
        onReload={() => {
          // After mutations, re-fetch /v1/me to reflect changes
          clearAuthQueryParams();
          reload();
        }}
      />
      {auth.inviteToken && (
        <InviteAcceptModal
          token={auth.inviteToken}
          me={auth.me}
          onAccepted={() => {
            clearAuthQueryParams();
            reload();
          }}
          onDismiss={() => {
            clearAuthQueryParams();
            setAuth({ ...auth, inviteToken: null });
          }}
        />
      )}
    </UndoProvider>
  );
}

export default App;
